import React, { useEffect, useState } from 'react';
import { Tree } from 'antd';
import type { DataNode, EventDataNode, TreeProps } from 'antd/es/tree';
import CoursePageItem from './CoursePageItem';
import { observer } from 'mobx-react';
import { createUseStyles } from 'react-jss';
import { TTreeItemDataViewModel, TTreeViewModel } from '../../../../services/models/CourseViewModels';
import IconButton from '@mui/material/IconButton';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { checkNumberOfChildren, checkNumberOfParents } from '../../../../utils/courseDashboard/coureDashboardUtils';
import { useStore } from '../../../../store/store';
import { useSnackbar } from 'notistack';
import { convertToAntdTree, getExpandedKeys } from '../../../../utils/pages/convertToAntdTree';

type NodeDragEventParams<CustomTreeNode> = {
  event: React.DragEvent;
  node: CustomTreeNode;
};

export interface IPageItem extends DataNode {
  id: number;
  data: TTreeItemDataViewModel;
  isExpanded: boolean;
  children: IPageItem[];
}

interface CoursePageListProps {
  pages: TTreeViewModel[];
  handleNewPageItem: (item: IPageItem) => void;
  handlePastePage: (item: IPageItem) => void;
  handleDuplicatePageItem: (item: IPageItem) => void;
  handleCopyPageItem: (item: IPageItem, copyChildren: boolean) => void;
  height: number;
  image: boolean;
}

const useStyles = createUseStyles({
  treeLayout: {
    '& .ant-tree-treenode': {
      width: '100%',
      minWidth: 0,
      overflow: 'visible',
    },
    '& .ant-tree-node-content-wrapper, & .ant-tree-title, & .ant-tree-title > div': {
      minWidth: 0,
    },
    '& .ant-tree-node-content-wrapper': {
      display: 'flex !important',
      flex: '1 1 0',
      width: '100%',
      minWidth: 0,
      overflow: 'visible',
    },
    '& .ant-tree-title': {
      display: 'block',
      width: '0',
      minWidth: 0,
      flex: '1 1 auto',
      overflow: 'visible',
    },
    '& .pageItem': {
      width: '100%',
      minWidth: 0,
      overflow: 'visible',
    },
  },
});

export default observer(function CoursePageList({ pages, handleNewPageItem, handlePastePage, handleDuplicatePageItem, handleCopyPageItem, height, image }: CoursePageListProps) {
  const classes = useStyles();
  const [treeData, setTreeData] = useState<IPageItem[]>(convertToAntdTree(pages));
  const [expandedKeys, setExpandedKeys] = useState<string[]>(getExpandedKeys(treeData));
  const [draggingKey, setDraggingKey] = useState<string>('');
  const { enqueueSnackbar } = useSnackbar();
  const { courseStore, pageStore } = useStore();
  const { courseViewModel, movePage } = courseStore;
  const { setExpanded } = pageStore;

  const findParentKey = (nodes: IPageItem[], childKey: string, parentKey = '0'): string | null => {
    for (const node of nodes) {
      if (String(node.key) === childKey) {
        return parentKey;
      }
      if (node.children?.length) {
        const found = findParentKey(node.children as IPageItem[], childKey, String(node.key));
        if (found) {
          return found;
        }
      }
    }
    return null;
  };

  const findNodeByKey = (nodes: IPageItem[], key: string): IPageItem | undefined => {
    for (const node of nodes) {
      if (String(node.key) === key) {
        return node;
      }
      if (node.children?.length) {
        const found = findNodeByKey(node.children, key);
        if (found) {
          return found;
        }
      }
    }
    return undefined;
  };

  const isInSubtree = (node: IPageItem, key: string): boolean => {
    if (String(node.key) === key) return true;
    if (!node.children?.length) return false;
    return node.children.some((child) => isInSubtree(child, key));
  };

  const normalizeTreeDataMetadata = (nodes: IPageItem[], parentID = 0) => {
    let siblingIndex = 0;
    for (const node of nodes) {
      if (String(node.key) === '__drop_spacer__') {
        continue;
      }

      node.data.parentCoursePageID = parentID;
      node.data.index = siblingIndex;
      siblingIndex += 1;

      const currentNodeID = Number.parseInt(String(node.key), 10);
      normalizeTreeDataMetadata(node.children, Number.isNaN(currentNodeID) ? 0 : currentNodeID);
    }
  };

  const cloneTreeNodes = (nodes: IPageItem[]): IPageItem[] => {
    return nodes.map((node) => ({
      ...node,
      data: { ...node.data },
      children: cloneTreeNodes(node.children),
    }));
  };

  const handleDrop: TreeProps['onDrop'] = async (info) => {
    const infoDragNode = info.dragNode as EventDataNode<IPageItem>;
    const dropKey = info.node.key as string;
    const dragKey = infoDragNode.key as string;
    const dropPosition = info.dropPosition;
    const dropToGap = info.dropToGap;

    if (dragKey === dropKey) {
      return;
    }

    const dragNodeInCurrentTree = findNodeByKey(treeData, dragKey);
    if (!dragNodeInCurrentTree) {
      return;
    }

    if (dropKey !== '__drop_spacer__' && isInSubtree(dragNodeInCurrentTree, dropKey)) {
      enqueueSnackbar('You cannot move a page into itself or one of its subpages.', {
        variant: 'info',
        autoHideDuration: 5000,
      });
      return;
    }

    let destinationIndex: undefined | number = undefined;
    let insertCompleted = false;

    const data = cloneTreeNodes(treeData); // Clone the tree data to avoid mutating current state

    // Utility to loop through tree and act on a specific key
    const loop = (nodes: IPageItem[], key: string, callback: (node: IPageItem, index: number, arr: IPageItem[]) => void): boolean => {
      let found = false;
      for (let i = 0; i < nodes.length; i++) {
        if (String(nodes[i].key) === key) {
          callback(nodes[i], i, nodes);
          return true;
        }
        if (nodes[i].children?.length) {
          found = loop(nodes[i].children as IPageItem[], key, callback);
          if (found) {
            return true;
          }
        }
      }
      return false;
    };

    // Remove dragNode from its current location and store it
    let dragNode: IPageItem | null = null;
    const dragNodeFound = loop(data, dragKey, (node, index, arr) => {
      arr.splice(index, 1); // Remove node from its original position
      dragNode = node;
    });

    if (!dragNodeFound || !dragNode) return;
    const draggedNode = dragNode;

    if (dropKey === '__drop_spacer__') {
      const spacerIndex = data.findIndex((node) => String(node.key) === '__drop_spacer__');
      const insertIndex = spacerIndex >= 0 ? spacerIndex : data.length;
      data.splice(insertIndex, 0, draggedNode);
      destinationIndex = insertIndex;
      insertCompleted = true;
    } else if (!dropToGap) {
      // Dropped inside a node - make it a **child** of the target node
      loop(data, dropKey, (node) => {
        node.children = node.children || [];
        const insertIndex = 0;
        node.children.splice(insertIndex, 0, draggedNode); // insert at index 0 or any logic
        destinationIndex = insertIndex;
        insertCompleted = true;
      });
    } else if (dropPosition === 0 || dropPosition === -1) {
      // Dropped **before** the node (same level as dropKey)
      loop(data, dropKey, (node, index, arr) => {
        arr.splice(index, 0, draggedNode); // Inserts dragNode before the target node in its parent's children array
        destinationIndex = index;
        insertCompleted = true;
      });
    } else {
      // Dropped **after** the node (same level as dropKey)
      loop(data, dropKey, (node, index, arr) => {
        arr.splice(index + 1, 0, draggedNode); // Inserts dragNode after the target node in its parent's children array
        destinationIndex = index + 1;
        insertCompleted = true;
      });
    }

    if (!insertCompleted || destinationIndex === undefined) {
      return;
    }

    // Determine source and destination from keys
    const coursePageID = infoDragNode.key.toString();
    const destinationParentId = findParentKey(data, coursePageID) ?? '0';

    // Check parent/child depth
    const parentCount = checkNumberOfParents(data, destinationParentId);
    const childDepth = checkNumberOfChildren(dragNode);

    if (parentCount + childDepth >= 3) {
      enqueueSnackbar('Maximum indent level reached. Please paste at a higher level.', {
        variant: 'info',
        autoHideDuration: 5000,
      });
      return;
    }

    const moved = await movePage(coursePageID, destinationParentId, destinationIndex);
    if (!moved) {
      enqueueSnackbar('Unable to move page. Please try again.', {
        variant: 'error',
      });
      return;
    }

    normalizeTreeDataMetadata(data);
    setTreeData(data); // Apply the modified tree structure
  };

  const handleExpand = (e: React.MouseEvent<HTMLDivElement, MouseEvent>, aPageID: string) => {
    e.stopPropagation();
    setTreeData((data) => data.map((node) => (node.key === aPageID ? { ...node, isExpanded: true } : node)));
    if (courseViewModel) {
      setExpanded(courseViewModel.courseID, aPageID, true);
    }

    setExpandedKeys((prevKeys) => {
      return prevKeys.includes(aPageID) ? prevKeys : [...prevKeys, aPageID];
    });
  };

  const handleExpandItem = (aInfo: { node: EventDataNode<IPageItem>; expanded: boolean; nativeEvent: MouseEvent }) => {
    const pageID = String(aInfo.node.key);

    setExpandedKeys((prevKeys) => {
      if (aInfo.expanded) {
        // Add the key if not already present
        return prevKeys.includes(pageID) ? prevKeys : [...prevKeys, pageID];
      } else {
        // Remove the key
        return prevKeys.filter((k) => k !== pageID);
      }
    });

    if (courseViewModel) {
      setExpanded(courseViewModel.courseID, pageID, aInfo.expanded);
    }
  };

  const [currentDragGhost, setCurrentDragGhost] = useState<HTMLElement>();
  const [dragOffset, setDragOffset] = useState({ x: 20, y: 20 });

  const handleDragStart = (aInfo: NodeDragEventParams<IPageItem>) => {
    const targetElement = aInfo.event.target instanceof HTMLElement ? aInfo.event.target : null;
    const isDragHandle = !!targetElement?.closest('[data-drag-handle="true"]');
    const isEditingTitle = !!targetElement?.closest('[data-title-editing="true"]');
    const activeElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const isEditingTitleFocused = !!activeElement?.closest('[data-title-editing="true"]');

    if ((isEditingTitle || isEditingTitleFocused) && !isDragHandle) {
      aInfo.event.preventDefault();
      aInfo.event.stopPropagation();
      return;
    }

    setDraggingKey(String(aInfo.node.key));
    const dragGhost = document.createElement('span');
    aInfo.event.dataTransfer.setDragImage(dragGhost, 0, 0); //Hides original ghost image

    const dragPage = (targetElement?.closest('[data-drag]') as HTMLDivElement | null) ?? (targetElement?.querySelector('[data-drag]') as HTMLDivElement | null);
    if (!dragPage) return;
    const cloneEl = dragPage.cloneNode(true) as HTMLElement;

    const bodyRect = document.body.getBoundingClientRect();
    const rect = dragPage.getBoundingClientRect();
    const offsetX = aInfo.event.clientX - (rect.x - bodyRect.left);
    const offsetY = aInfo.event.clientY - (rect.y - bodyRect.top);
    setDragOffset({ x: offsetX, y: offsetY });

    cloneEl.style.position = 'absolute';
    cloneEl.style.pointerEvents = 'none';
    cloneEl.style.userSelect = 'none';
    cloneEl.style.zIndex = '999999';
    cloneEl.style.left = '-10000px';
    cloneEl.style.top = '-10000px';
    cloneEl.style.opacity = '0.6';
    cloneEl.style.width = dragPage.getBoundingClientRect().width + 'px';
    cloneEl.id = 'currentTreeItemDragElement';
    setCurrentDragGhost(cloneEl);
    document.body.appendChild(cloneEl);
  };

  const handleDragEnd = () => {
    setDraggingKey('');
    if (!currentDragGhost) return;
    currentDragGhost.remove();
    setCurrentDragGhost(undefined);
  };

  useEffect(() => {
    const handleDrag = (e: any) => {
      if (!currentDragGhost) return;
      const setPosition = (value: number) => value || -10000;
      currentDragGhost.style.left = setPosition(e.clientX) - dragOffset.x + 'px';
      currentDragGhost.style.top = setPosition(e.clientY) - dragOffset.y + 'px';
    };

    window.addEventListener('drag', handleDrag);
    return () => {
      window.removeEventListener('drag', handleDrag);
    };
  }, [currentDragGhost]);

  useEffect(() => {
    const convertedTree = convertToAntdTree(pages);
    setTreeData(convertedTree);
    setExpandedKeys(getExpandedKeys(convertedTree));
  }, [pages]);

  return (
    <div className={classes.treeLayout} style={{ maxHeight: height, paddingTop: 3 }}>
      <Tree
        treeData={treeData}
        draggable={{ icon: false }}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onExpand={(e, info) => handleExpandItem(info)}
        onDrop={handleDrop}
        blockNode
        switcherIcon={({ expanded, data }) => {
          return (
            <IconButton size='small' style={{ transform: expanded ? 'rotate(180deg)' : 'rotate(180deg)', marginBottom: 6, viewTransitionName: `page-switch-${data.key}` }}>
              <ExpandLessIcon />
            </IconButton>
          );
        }}
        expandedKeys={expandedKeys}
        titleRender={(node) => {
          if (node.key === '__drop_spacer__') {
            return (
              <div
                style={{
                  height: 50,
                  marginTop: 0,
                  backgroundColor: 'transparent',
                  cursor: 'default',
                }}
              />
            );
          }
          return (
            <CoursePageItem
              nodeData={node as IPageItem}
              onExpand={handleExpand}
              handleNewPageItem={handleNewPageItem}
              handlePastePage={handlePastePage}
              handleCopyPageItem={handleCopyPageItem}
              handleDuplicatePageItem={handleDuplicatePageItem}
              draggingKey={draggingKey}
              expandedKeys={expandedKeys}
              image={image}
            />
          );
        }}
        rootStyle={{ backgroundColor: 'transparent' }}
      />
    </div>
  );
});
